import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Form, Input, DatePicker, message } from 'antd';
import { ILicenseData, LicenseData } from 'types/newUniprofile';
import FormValidationHelper from 'utils/validation';
import FileUpload from 'components/form/FileUpload';
import LicenseImg from 'components/LicenseImg';
import s from './s.module.less';
import dayjs from 'dayjs';
import { formatDate } from 'utils/common';
import { ELicenseType } from 'types/common';
import { providerLicenseUpdate } from 'api/provider';
import { getUserId } from 'utils/localstore';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import { isEqual } from 'lodash';

interface Props {
    initValue?: ILicenseData;
    isEditing?: boolean;
    refetch?: () => void;
    scheduleToken?: string;
}

const MalpracticeInsuranceCertificate = forwardRef((props: Props, ref) => {
    const { isEditing, initValue, refetch, scheduleToken } = props;
    const [form] = Form.useForm<LicenseData>();
    const prevInitValueRef = useRef<ILicenseData>();

    useEffect(() => {
        if (initValue && !isEqual(prevInitValueRef.current, initValue)) {
            prevInitValueRef.current = initValue;
            form.setFieldsValue({
                ...initValue,
                expireDate: initValue?.expireDate ? dayjs(initValue?.expireDate) : undefined,
            });
        }
    }, [initValue]);

    const onSubmit = async () => {
        try {
            const params = await form.validateFields();
            const licenseData: ILicenseData = {
                ...params,
                expireDate: formatDate(params.expireDate),
                type: ELicenseType.MIC,
                licenseType: '',
            };
            const res = await providerLicenseUpdate(licenseData, initValue?.providerId ?? getUserId()!, scheduleToken);
            if (res.error) {
                message.error(res.error);
            } else {
                message.success('Malpractice insurance certificate has been updated');
                refetch?.();
            }
        } catch (e) {
            console.error(e);
            throw e;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
        onValidate: () => form.validateFields(),
    }));

    const renderEditing = () => {
        return (
            <div className={s.wrap}>
                <Form form={form} initialValues={{ remember: true }} autoComplete="off" layout="vertical">
                    <Form.Item noStyle>
                        <div className="flex gap-6">
                            <Form.Item name="id" style={{ position: 'absolute' }}>
                                <Input type="hidden" />
                            </Form.Item>
                            <Form.Item
                                style={{ width: '320px' }}
                                label="Certificate number"
                                name="licenseNumber"
                                validateFirst
                                rules={[
                                    { required: true, message: 'Certificate number is a required field' },
                                    FormValidationHelper.validateLicenseNumber('Must contain only letters or numbers'),
                                ]}
                            >
                                <Input maxLength={50} placeholder="" />
                            </Form.Item>
                            <Form.Item
                                style={{ width: '320px' }}
                                label="Expiration date"
                                name="expireDate"
                                rules={[{ required: true, message: 'Expiration date is a required field' }]}
                            >
                                <DatePicker
                                    disabledDate={(date) => {
                                        const now = new Date().getTime();
                                        if (!date) {
                                            return false;
                                        }
                                        return date?.valueOf() < now;
                                    }}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </div>

                        <Form.Item
                            style={{ width: '300px' }}
                            label="Certificate copy"
                            name="url"
                            rules={[{ required: true, message: 'Certificate copy is a required field' }]}
                        >
                            <FileUploadPrivate uploadType="LICENSE" />
                            {/* <FileUpload /> */}
                        </Form.Item>
                    </Form.Item>
                </Form>
            </div>
        );
    };

    const renderReview = () => {
        if (!initValue?.licenseNumber) {
            return <div>Not provided</div>;
        }
        return (
            <div className={s.subModule} key={initValue?.licenseNumber}>
                <div className={s.row}>
                    <div
                        style={{
                            width: '300px',
                        }}
                        className={s.displayItem}
                    >
                        <div className={s.label}>Certificate number</div>
                        <div className={s.value}>{initValue?.licenseNumber}</div>
                    </div>
                    <div className={s.displayItem}>
                        <div className={s.label}>Expiration date</div>
                        <div className={s.value}>
                            {initValue?.expireDate ? dayjs(initValue?.expireDate).format('MM-DD-YYYY') : ''}
                        </div>
                    </div>
                </div>

                <div className={s.row}>
                    <div
                        style={{
                            width: '80%',
                        }}
                        className={s.displayItem}
                    >
                        <div className={s.label}>Certificate copy</div>
                        <div className={s.value}>
                            <LicenseImg src={initValue?.fullUrl || initValue?.url} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return <div>{isEditing ? renderEditing() : renderReview()}</div>;
});

export default MalpracticeInsuranceCertificate;
